/**
 * Engagement beacon payload — client → edge ingest for impressions/views/clicks.
 *
 * The wire types are the **string-value unions of the generated enums**, derived
 * via `import type` (erased at compile time) so this shared payload stays a plain
 * JSON contract — no generated module is pulled into the edge or browser bundle —
 * while still tracking the enums exactly (a new enum value flows through here).
 */

import type {
  EngagementEntityTypesEnum,
  EngagementEventTypesEnum,
  EngagementSourcesEnum,
} from 'types/generated/server';

export type EngagementEntityType = `${EngagementEntityTypesEnum}`;
export type EngagementEventType = `${EngagementEventTypesEnum}`;
export type EngagementSource = `${EngagementSourcesEnum}`;

export interface EngagementEventInput {
  entityType: EngagementEntityType;
  /** Program id (LESSON/EVENT), venue id (CLUB_PAGE), or coach user id (COACH_PROFILE). */
  entityId: string;
  eventType: EngagementEventType;
  source: EngagementSource;
  /** Boost line-item id when the engagement came through a boosted placement. */
  placementId?: string | null;
  /** Client event time (ISO). */
  occurredAt: string;
}

export interface EngagementBeaconRequestPayload {
  /** Stable per-browser id for logged-out dedup; ignored when a viewer token is present. */
  anonSessionId?: string | null;
  events: EngagementEventInput[];
}

export interface EngagementBeaconResponsePayload {
  accepted: number;
}

/** Max events accepted per beacon call (drops the overflow defensively). */
export const ENGAGEMENT_BEACON_MAX_EVENTS = 100;

export const ENGAGEMENT_BEACON_API = 'v1/analytics/engagement';

/**
 * Query-param key carrying the engagement source across a navigation, e.g. a
 * marketplace card links to `/events/:id?src=BROWSE`. The detail page reads it
 * to attribute its view/cta_click to where the user actually came from. Absent
 * (direct nav, bookmark) resolves to `OTHER`.
 */
export const ENGAGEMENT_SOURCE_PARAM = 'src';

/**
 * Append the engagement source to an event/detail URL, e.g.
 * `withEngagementSource(getEventUrl(id), 'BROWSE')`. Handles `?`/`&` so it's safe
 * on URLs that already carry params (`?invite=`, `?team=`). Pure string work — usable
 * on the client and in server-side email/share builders. `source` is type-checked
 * against the enum, so no stringly-typed values leak in.
 */
export const withEngagementSource = (url: string, source: EngagementSource) =>
  `${url}${url.includes('?') ? '&' : '?'}${ENGAGEMENT_SOURCE_PARAM}=${source}`;
